import React from 'react';
import { motion } from 'framer-motion';
import { Search, Filter, Building2, BadgeCheck, Check, SlidersHorizontal, Mail, Phone, Lock, Star, FileText, Copy, ChevronDown, TrendingUp } from 'lucide-react';
// Platform Icons
const TTIco = () => <img src="https://cdn.magicpatterns.com/patterns/figma-images/HPPMfQF9zbBNm5oHh69dtz/362-1248.png" alt="TikTok" className="w-3.5 h-3.5 inline-block" data-id="element-921" />;
const IGIco = () => <img src="https://cdn.magicpatterns.com/patterns/figma-images/HPPMfQF9zbBNm5oHh69dtz/362-1249.png" alt="Instagram" className="w-3.5 h-3.5 inline-block" data-id="element-922" />;
const YTIco = () => <svg className="w-4 h-4 inline-block text-[#FF0000]" viewBox="0 0 24 24" fill="currentColor" aria-label="YouTube" data-id="element-923">
    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" data-id="element-924" />
  </svg>;
// A window chrome header (traffic-light dots) instead of the app sidebar/tab menu.
const WindowBar = ({
  title,
  approved = false
}: {
  title: string;
  approved?: boolean;
}) => <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-100 bg-gray-50/70" data-id="element-925">
    <div className="flex items-center gap-1.5" data-id="element-926">
      <span className="w-2.5 h-2.5 rounded-full bg-red-400/70" data-id="element-927" />
      <span className="w-2.5 h-2.5 rounded-full bg-amber-400/70" data-id="element-928" />
      <span className="w-2.5 h-2.5 rounded-full bg-emerald-400/70" data-id="element-929" />
    </div>
    <span className="ml-2 text-xs font-semibold text-gray-500" data-id="element-930">{title}</span>
    {approved && <span className="ml-auto inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 text-[9px] font-semibold uppercase tracking-wider" data-id="element-931">
        <Lock size={8} data-id="element-932" /> Approved Access
      </span>}
  </div>;
// Reusable float animation for the small overlapping cards.
const floatY = (delay = 0, distance = 8) => ({
  animate: {
    y: [0, -distance, 0]
  },
  transition: {
    duration: 4,
    repeat: Infinity,
    ease: 'easeInOut' as const,
    delay
  }
});
// 1) ROSTER DATABASE — a card-grid gallery of creators (distinct from the homepage table),
// with a floating live-filters chip and a floating agency-contact chip.
export const LayeredRosterDatabaseMockup = () => {
  const creators = [{
    name: 'Michelle Brown',
    cat: 'Fashion',
    followers: '2.2M',
    photo: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=160&h=160'
  }, {
    name: 'Elena Rodriguez',
    cat: 'Beauty',
    followers: '1.2M',
    photo: 'https://images.unsplash.com/photo-1524504388940-b1c1722653e1?auto=format&fit=crop&w=160&h=160'
  }, {
    name: 'Sarah Jenkins',
    cat: 'Lifestyle',
    followers: '890K',
    photo: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&w=160&h=160'
  }, {
    name: 'Aisha Kohl',
    cat: 'Fitness',
    followers: '640K',
    photo: 'https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?auto=format&fit=crop&w=160&h=160'
  }];
  return <div className="relative h-[440px] w-full" data-id="element-933">
      <div className="absolute -inset-6 bg-gradient-to-tr from-zano-pink/10 via-zano-lavender/20 to-sky-100/20 rounded-[2.5rem] blur-2xl -z-10" data-id="element-934" />
      <div className="absolute inset-0 bg-white rounded-2xl border border-gray-200 shadow-float overflow-hidden flex flex-col text-left" data-id="element-935">
        <WindowBar title="Roster Database" approved data-id="element-936" />
        {/* search + filter chips */}
        <div className="p-4 pb-3" data-id="element-937">
          <div className="flex items-center gap-2" data-id="element-938">
            <div className="relative flex-1" data-id="element-939">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-940" />
              <input type="text" placeholder="Search 6,000+ creators" className="w-full pl-8 pr-2 py-2 border border-gray-200 rounded-lg text-xs outline-none bg-gray-50" data-id="element-941" />
            </div>
            <button className="flex items-center gap-1 border border-zano-pink/30 bg-zano-blush text-zano-pink px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap" data-id="element-942">
              <SlidersHorizontal size={12} data-id="element-943" /> Filters
            </button>
          </div>
          <div className="flex flex-wrap gap-1.5 mt-2.5" data-id="element-944">
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-emerald-50 text-emerald-700 border border-emerald-200" data-id="element-945">
              <BadgeCheck size={10} data-id="element-946" /> Exclusive only
            </span>
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 text-gray-600 border border-gray-200" data-id="element-947">
              Fashion
            </span>
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 text-gray-600 border border-gray-200" data-id="element-948">
              100K+ followers
            </span>
          </div>
        </div>
        {/* card gallery */}
        <div className="px-4 pb-4 grid grid-cols-2 gap-3 overflow-hidden" data-id="element-949">
          {creators.map((c, i) => <div key={i} className="border border-gray-100 rounded-xl p-3 bg-white hover:border-zano-pink/30 transition-colors" data-id="element-950">
              <div className="flex items-center gap-2.5" data-id="element-951">
                <img src={c.photo} alt={c.name} className="w-11 h-11 rounded-full object-cover" data-id="element-952" />
                <div className="min-w-0" data-id="element-953">
                  <p className="text-xs font-semibold text-gray-900 truncate" data-id="element-954">
                    {c.name}
                  </p>
                  <p className="text-[10px] text-gray-500" data-id="element-955">{c.cat}</p>
                </div>
              </div>
              <div className="flex items-center justify-between mt-2.5" data-id="element-956">
                <span className="flex items-center gap-1 text-[10px] font-medium text-gray-600" data-id="element-957">
                  <TTIco data-id="element-958" /> {c.followers}
                </span>
                <span className="inline-flex items-center gap-0.5 text-[9px] font-semibold text-emerald-600" data-id="element-959">
                  <BadgeCheck size={9} data-id="element-960" /> Exclusive
                </span>
              </div>
            </div>)}
        </div>
      </div>
      {/* Floating live-stats chip */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.5,
      delay: 0.3
    }} className="absolute -top-4 -left-4 sm:-left-6 z-20" data-id="element-961">
        <motion.div {...floatY(0.2)} className="bg-white rounded-xl border border-gray-200 shadow-[0_20px_40px_-15px_rgba(0,0,0,0.15)] p-3 w-44" data-id="element-962">
          <div className="flex items-center gap-1.5 mb-2" data-id="element-963">
            <TrendingUp size={13} className="text-zano-pink" data-id="element-964" />
            <p className="text-[11px] font-bold text-gray-900" data-id="element-965">Live stats</p>
            <span className="ml-auto w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" data-id="element-966" />
          </div>
          <div className="space-y-1.5" data-id="element-967">
            <div className="flex justify-between text-[10px]" data-id="element-968">
              <span className="text-gray-500" data-id="element-969">Avg. views</span>
              <span className="font-semibold text-gray-900" data-id="element-970">142K</span>
            </div>
            <div className="flex justify-between text-[10px]" data-id="element-971">
              <span className="text-gray-500" data-id="element-972">Eng. rate</span>
              <span className="font-semibold text-gray-900" data-id="element-973">5.3%</span>
            </div>
          </div>
        </motion.div>
      </motion.div>
      {/* Floating agency-contact chip */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.5,
      delay: 0.5
    }} className="absolute -bottom-5 -right-4 sm:-right-6 z-20" data-id="element-974">
        <motion.div {...floatY(0.9)} className="bg-white rounded-xl border border-gray-200 shadow-[0_20px_40px_-15px_rgba(0,0,0,0.15)] p-3 w-52" data-id="element-975">
          <p className="text-[9px] font-semibold text-gray-500 uppercase tracking-wider mb-1" data-id="element-976">
            Talent manager
          </p>
          <div className="flex items-center justify-between gap-2 bg-zano-blush/60 border border-zano-pink/20 rounded-md px-2 py-1.5" data-id="element-977">
            <span className="flex items-center gap-1.5 text-[10px] font-medium text-zano-charcoal truncate" data-id="element-978">
              <Mail size={11} className="text-zano-pink shrink-0" data-id="element-979" />
              john@abctalentagency.com
            </span>
            <Copy size={10} className="text-gray-400 shrink-0" data-id="element-980" />
          </div>
        </motion.div>
      </motion.div>
    </div>;
};
// 2) A-LIST TALENT — a focused search result surfacing one premium profile (generic data),
// with a floating "A-List verified" badge that floats.
export const LayeredAListMockup = () => {
  return <div className="relative h-[440px] w-full" data-id="element-981">
      <div className="absolute -inset-6 bg-gradient-to-tr from-amber-100/40 via-zano-pink/20 to-zano-lavender/30 rounded-[2.5rem] blur-2xl -z-10" data-id="element-982" />
      <div className="absolute inset-0 bg-white rounded-2xl border border-gray-200 shadow-float overflow-hidden flex flex-col text-left" data-id="element-983">
        <WindowBar title="Roster Database" approved data-id="element-984" />
        <div className="p-4" data-id="element-985">
          {/* search bar showing a query */}
          <div className="relative w-full" data-id="element-986">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zano-pink" data-id="element-987" />
            <div className="w-full pl-9 pr-3 py-2.5 border-2 border-zano-pink/40 rounded-lg text-sm font-medium text-gray-900 bg-white shadow-sm" data-id="element-988">
              Michelle Brown
            </div>
          </div>
          <p className="text-[11px] text-gray-400 mt-2 mb-3" data-id="element-989">1 result</p>
          {/* the premium profile result */}
          <div className="border border-gray-200 rounded-xl overflow-hidden" data-id="element-990">
            <div className="p-4 flex items-center gap-3 border-b border-gray-100" data-id="element-991">
              <img src="https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=120&h=120" alt="Michelle Brown" className="w-14 h-14 rounded-full object-cover" data-id="element-992" />
              <div className="min-w-0" data-id="element-993">
                <h3 className="text-base font-bold text-gray-900 leading-tight" data-id="element-994">
                  Michelle Brown
                </h3>
                <div className="flex items-center gap-2 mt-1" data-id="element-995">
                  <span className="flex items-center gap-1 text-[11px] font-medium text-gray-600" data-id="element-996">
                    <TTIco data-id="element-997" /> 8.4M
                  </span>
                  <span className="flex items-center gap-1 text-[11px] font-medium text-gray-600" data-id="element-998">
                    <IGIco data-id="element-999" /> 3.1M
                  </span>
                </div>
              </div>
            </div>
            <div className="p-4" data-id="element-1000">
              <div className="flex items-center gap-2 mb-2" data-id="element-1001">
                <div className="w-7 h-7 bg-gray-900 text-white rounded-md flex items-center justify-center text-[9px] font-bold" data-id="element-1002">
                  ABC
                </div>
                <span className="text-sm font-bold text-gray-900" data-id="element-1003">
                  ABC Talent Agency
                </span>
                <span className="ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[10px] font-semibold" data-id="element-1004">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" data-id="element-1005" />
                  Exclusive
                </span>
              </div>
              <div className="space-y-2 mt-3" data-id="element-1006">
                <p className="text-[9px] font-semibold text-gray-500 uppercase tracking-wider" data-id="element-1007">
                  Talent manager · John Smith
                </p>
                <div className="flex items-center justify-between gap-2 bg-gray-50 border border-gray-200 rounded-md px-2.5 py-1.5" data-id="element-1008">
                  <span className="flex items-center gap-2 text-xs font-medium text-gray-700 truncate" data-id="element-1009">
                    <Mail size={12} className="text-gray-400 shrink-0" data-id="element-1010" />
                    john@abctalentagency.com
                  </span>
                  <Copy size={12} className="text-gray-400 shrink-0" data-id="element-1011" />
                </div>
                <div className="flex items-center justify-between gap-2 bg-gray-50 border border-gray-200 rounded-md px-2.5 py-1.5" data-id="element-1012">
                  <span className="flex items-center gap-2 text-xs font-medium text-gray-700 truncate" data-id="element-1013">
                    <Phone size={12} className="text-gray-400 shrink-0" data-id="element-1014" />
                    +1 (555) 018-2934
                  </span>
                  <Copy size={12} className="text-gray-400 shrink-0" data-id="element-1015" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Floating A-List badge */}
      <motion.div initial={{
      opacity: 0,
      scale: 0.9
    }} whileInView={{
      opacity: 1,
      scale: 1
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.5,
      delay: 0.4
    }} className="absolute -top-4 -right-3 sm:-right-5 z-20" data-id="element-1016">
        <motion.div {...floatY(0.3, 6)} className="flex items-center gap-1.5 bg-white rounded-full border border-amber-200 shadow-[0_15px_35px_-12px_rgba(0,0,0,0.2)] pl-2 pr-3 py-1.5" data-id="element-1017">
          <span className="w-6 h-6 rounded-full bg-amber-100 flex items-center justify-center" data-id="element-1018">
            <Star size={12} className="fill-amber-500 text-amber-500" data-id="element-1019" />
          </span>
          <span className="text-[11px] font-bold text-amber-700" data-id="element-1020">
            A-List talent
          </span>
        </motion.div>
      </motion.div>
      {/* Floating quick-book chip */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.5,
      delay: 0.6
    }} className="absolute bottom-2 -left-4 sm:-left-6 z-20" data-id="element-1021">
        <motion.div {...floatY(1.1)} className="bg-white rounded-xl border border-gray-200 shadow-[0_20px_40px_-15px_rgba(0,0,0,0.15)] p-3 w-40" data-id="element-1022">
          <div className="flex items-center gap-1.5 mb-1" data-id="element-1023">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" data-id="element-1024" />
            <p className="text-[10px] font-bold text-gray-900" data-id="element-1025">
              Fast turnaround
            </p>
          </div>
          <p className="text-[10px] text-gray-500 leading-snug" data-id="element-1026">
            Direct line to the manager — no middlemen.
          </p>
        </motion.div>
      </motion.div>
    </div>;
};
// 3) CASTING CALLS — a brief composer panel as the base, with a floating incoming-submission
// card and a floating "posted to 400+ agencies" pulse.
export const LayeredCastingCallMockup = () => {
  return <div className="relative h-[440px] w-full" data-id="element-1027">
      <div className="absolute -inset-6 bg-gradient-to-tr from-sky-100/40 via-zano-lavender/30 to-zano-pink/10 rounded-[2.5rem] blur-2xl -z-10" data-id="element-1028" />
      <div className="absolute inset-0 bg-white rounded-2xl border border-gray-200 shadow-float overflow-hidden flex flex-col text-left" data-id="element-1029">
        <WindowBar title="New Casting Call" data-id="element-1030" />
        <div className="p-4 space-y-3 overflow-hidden" data-id="element-1031">
          <div data-id="element-1032">
            <label className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider" data-id="element-1033">
              Campaign
            </label>
            <div className="mt-1 text-xs text-gray-800 border border-gray-200 rounded-md px-3 py-2 bg-gray-50" data-id="element-1034">
              Summer Skincare Launch
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3" data-id="element-1035">
            <div data-id="element-1036">
              <label className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider" data-id="element-1037">
                Budget
              </label>
              <div className="mt-1 text-xs text-gray-800 border border-gray-200 rounded-md px-3 py-2 bg-gray-50" data-id="element-1038">
                $10k – $20k
              </div>
            </div>
            <div data-id="element-1039">
              <label className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider" data-id="element-1040">
                Platforms
              </label>
              <div className="mt-1 flex gap-1.5 border border-gray-200 rounded-md px-3 py-2 bg-gray-50" data-id="element-1041">
                <TTIco data-id="element-1042" /> <IGIco data-id="element-1043" />
              </div>
            </div>
          </div>
          <div data-id="element-1044">
            <label className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider" data-id="element-1045">
              Influencer requirements
            </label>
            <div className="mt-1 text-xs text-gray-600 border border-gray-200 rounded-md px-3 py-2 bg-gray-50" data-id="element-1046">
              Beauty · US-based · 100K+ followers
            </div>
          </div>
          <div data-id="element-1047">
            <label className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider" data-id="element-1048">
              Deliverables
            </label>
            <div className="mt-1 text-xs text-gray-600 border border-gray-200 rounded-md px-3 py-2 bg-gray-50 h-11" data-id="element-1049">
              1x TikTok video, 1x IG story with link…
            </div>
          </div>
          <button className="w-full bg-zano-pink text-white rounded-md py-2.5 text-xs font-bold shadow-sm" data-id="element-1050">
            Post casting call
          </button>
        </div>
      </div>
      {/* Floating "posted to agencies" pulse */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.5,
      delay: 0.3
    }} className="absolute -top-4 -right-3 sm:-right-6 z-20" data-id="element-1051">
        <motion.div {...floatY(0.2)} className="bg-white rounded-xl border border-gray-200 shadow-[0_20px_40px_-15px_rgba(0,0,0,0.15)] p-3 w-44" data-id="element-1052">
          <div className="flex items-center gap-1.5 mb-1" data-id="element-1053">
            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" data-id="element-1054" />
            <p className="text-[10px] font-bold text-gray-900" data-id="element-1055">
              Posted to 400+ agencies
            </p>
          </div>
          <p className="text-[10px] text-gray-500" data-id="element-1056">
            Submissions rolling in within hours.
          </p>
        </motion.div>
      </motion.div>
      {/* Floating incoming-submission card */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.5,
      delay: 0.55
    }} className="absolute -bottom-6 -left-4 sm:-left-8 z-30" data-id="element-1057">
        <motion.div {...floatY(1)} className="bg-white rounded-xl border border-gray-200 shadow-[0_20px_40px_-15px_rgba(0,0,0,0.15)] p-3 w-56" data-id="element-1058">
          <div className="flex items-center gap-2.5 mb-2" data-id="element-1059">
            <img src="https://images.unsplash.com/photo-1524504388940-b1c1722653e1?auto=format&fit=crop&w=64&h=64" alt="Submission" className="w-9 h-9 rounded-full object-cover" data-id="element-1060" />
            <div className="min-w-0" data-id="element-1061">
              <p className="text-[11px] font-bold text-gray-900 truncate" data-id="element-1062">
                New submission
              </p>
              <span className="inline-flex items-center gap-1 text-[9px] font-semibold text-emerald-700" data-id="element-1063">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" data-id="element-1064" />
                Exclusive · ABC Talent Agency
              </span>
            </div>
          </div>
          <div className="flex justify-between items-center bg-gray-50 rounded-md border border-gray-100 px-2.5 py-1.5" data-id="element-1065">
            <span className="text-[10px] text-gray-500" data-id="element-1066">Quote</span>
            <span className="text-xs font-bold text-green-600" data-id="element-1067">$1,200</span>
          </div>
        </motion.div>
      </motion.div>
    </div>;
};
// 4) TALENT AGENCY DIRECTORY — a leaderboard-style ranked list of generic agencies,
// with a floating agency profile card.
export const LayeredDirectoryMockup = () => {
  const agencies = [{
    name: 'ABC Talent Agency',
    short: 'ABC',
    creators: '895',
    following: '3.2B'
  }, {
    name: 'Nova Management',
    short: 'NM',
    creators: '642',
    following: '1.6B'
  }, {
    name: 'Peak Creators Co.',
    short: 'PC',
    creators: '410',
    following: '1.1B'
  }, {
    name: 'Bright Talent Group',
    short: 'BT',
    creators: '188',
    following: '420M'
  }];
  return <div className="relative h-[440px] w-full" data-id="element-1068">
      <div className="absolute -inset-6 bg-gradient-to-tr from-purple-100/40 via-zano-lavender/30 to-blue-100/20 rounded-[2.5rem] blur-2xl -z-10" data-id="element-1069" />
      <div className="absolute inset-0 bg-white rounded-2xl border border-gray-200 shadow-float overflow-hidden flex flex-col text-left" data-id="element-1070">
        <WindowBar title="Talent Agency Directory" approved data-id="element-1071" />
        <div className="p-4" data-id="element-1072">
          <div className="flex items-center justify-between mb-3" data-id="element-1073">
            <p className="text-xs font-bold text-gray-900 flex items-center gap-1.5" data-id="element-1074">
              <Building2 size={13} className="text-zano-pink" data-id="element-1075" /> 400+ vetted
              agencies
            </p>
            <button className="flex items-center gap-1 border border-gray-200 bg-white px-2 py-1 rounded text-[10px] text-gray-600" data-id="element-1076">
              Ranked by reach <ChevronDown size={10} data-id="element-1077" />
            </button>
          </div>
          <div className="space-y-2" data-id="element-1078">
            {agencies.map((a, i) => <div key={i} className="flex items-center gap-3 border border-gray-100 rounded-xl px-3 py-2.5 hover:border-zano-pink/30 transition-colors" data-id="element-1079">
                <span className="text-xs font-bold text-gray-400 w-4" data-id="element-1080">
                  {i + 1}
                </span>
                <div className="w-8 h-8 bg-gray-900 text-white rounded-lg flex items-center justify-center text-[9px] font-bold shrink-0" data-id="element-1081">
                  {a.short}
                </div>
                <div className="min-w-0 flex-1" data-id="element-1082">
                  <p className="text-xs font-semibold text-gray-900 truncate" data-id="element-1083">
                    {a.name}
                  </p>
                  <p className="text-[10px] text-gray-500" data-id="element-1084">
                    {a.creators} creators
                  </p>
                </div>
                <div className="text-right shrink-0" data-id="element-1085">
                  <p className="text-xs font-bold text-gray-900" data-id="element-1086">
                    {a.following}
                  </p>
                  <p className="text-[9px] text-gray-400" data-id="element-1087">total reach</p>
                </div>
              </div>)}
          </div>
        </div>
      </div>
      {/* Floating agency profile card */}
      <motion.div initial={{
      opacity: 0,
      y: 20,
      x: -10
    }} whileInView={{
      opacity: 1,
      y: 0,
      x: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.5,
      delay: 0.4
    }} className="absolute -bottom-6 -right-4 sm:-right-8 z-20" data-id="element-1088">
        <motion.div {...floatY(0.6)} className="bg-white rounded-xl border border-gray-200 shadow-[0_20px_40px_-15px_rgba(0,0,0,0.15)] p-4 w-60" data-id="element-1089">
          <div className="flex items-center gap-2.5 mb-3" data-id="element-1090">
            <div className="w-10 h-10 bg-gray-900 text-white rounded-lg flex items-center justify-center text-xs font-bold" data-id="element-1091">
              ABC
            </div>
            <div data-id="element-1092">
              <h4 className="text-sm font-bold text-gray-900 leading-tight" data-id="element-1093">
                ABC Talent Agency
              </h4>
              <p className="text-[10px] text-gray-500" data-id="element-1094">Los Angeles, CA</p>
            </div>
          </div>
          <div className="flex items-center justify-between gap-2 bg-gray-50 border border-gray-200 rounded-md px-2.5 py-1.5" data-id="element-1095">
            <span className="flex items-center gap-1.5 text-[10px] font-medium text-gray-700 truncate" data-id="element-1096">
              <Mail size={11} className="text-gray-400 shrink-0" data-id="element-1097" />
              contact@abctalentagency.com
            </span>
            <Copy size={10} className="text-gray-400 shrink-0" data-id="element-1098" />
          </div>
        </motion.div>
      </motion.div>
    </div>;
};